import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ProfileCategory, getCategoryForProfile, PROFILE_CATEGORIES } from '@/utils/profileCategories';
import { profileTypeMapping } from '@/utils/profileTypeMapping';
import { useCompany } from '@/contexts/CompanyContext';

export interface ProfileDistributionData {
  profileName: string;
  pattern: string;
  count: number;
  percentage: number;
  color: string;
  category: ProfileCategory;
}

// Legacy name mapping for backward compatibility
const LEGACY_NAME_MAPPING: Record<string, string> = {
  'facilitator': 'Steady'
};

export const useProfileDistribution = (scope: 'company' | 'all' = 'company', modelFilter: 'all' | 'model1' | 'model2' | 'model3' = 'all') => {
  const [data, setData] = useState<ProfileDistributionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const { currentCompany } = useCompany();

  useEffect(() => {
    const fetchDistribution = async () => {
      if (scope === 'company' && !currentCompany?.company_id) {
        setData([]);
        setTotalCandidates(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        let query = supabase
          .from('survey_submissions')
          .select('profile_name, disc_code, selected_words, assessment_variant')
          .eq('assessment_type', 'behavioral')
          .not('disc_code', 'is', null);

        if (modelFilter !== 'all') {
          query = query.eq('assessment_variant', modelFilter);
        }

        if (scope === 'company' && currentCompany?.company_id) {
          query = query.eq('company_id', currentCompany.company_id);
        }

        const { data: submissions, error } = await query;

        if (error) throw error;

        // Count occurrences by disc_code, filtering out submissions with less than 10 words selected
        const profileCounts: Record<string, { count: number; name: string }> = {};
        
        submissions?.forEach((submission) => {
          if (submission.disc_code) {
            // Count selected words - handle various formats
            let wordCount = 0;
            if (submission.selected_words) {
              if (Array.isArray(submission.selected_words)) {
                // Old format: flat array of word IDs
                wordCount = submission.selected_words.length;
              } else if (typeof submission.selected_words === 'object') {
                const sw = submission.selected_words as { self?: number[]; work?: number[] };
                // Check for dual assessment structure { self: [...], work: [...] }
                if (Array.isArray(sw.self) && Array.isArray(sw.work)) {
                  // Count SELF + WORK combined
                  wordCount = sw.self.length + sw.work.length;
                } else if (Array.isArray(sw.self)) {
                  wordCount = sw.self.length;
                } else if (Array.isArray(sw.work)) {
                  wordCount = sw.work.length;
                } else {
                  // Fallback for legacy object format with word IDs as keys
                  wordCount = Object.keys(submission.selected_words).length;
                }
              }
            }
            
            // Skip submissions with less than 10 words selected
            if (wordCount < 10) return;
            
            const pattern = submission.disc_code;
            // Always use canonical mapping from disc_code (authoritative source)
            const profileName = profileTypeMapping[pattern]?.type || 'Unknown';
            
            if (!profileCounts[pattern]) {
              profileCounts[pattern] = { count: 0, name: profileName };
            }
            profileCounts[pattern].count += 1;
          }
        });

        const totalCount = Object.values(profileCounts).reduce((sum, data) => sum + data.count, 0);
        setTotalCandidates(totalCount);

        const distributionData: ProfileDistributionData[] = Object.entries(profileCounts)
          .map(([pattern, { count, name }]) => {
            const category = getCategoryForProfile(pattern);

            return {
              profileName: name,
              pattern,
              count,
              percentage: parseFloat(((count / totalCount) * 100).toFixed(1)),
              color: PROFILE_CATEGORIES[category].color,
              category
            };
          })
          .sort((a, b) => b.count - a.count);

        setData(distributionData);
      } catch (error) {
        console.error('Error fetching profile distribution:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDistribution();
  }, [currentCompany?.company_id, scope, modelFilter]);

  return { data, loading, totalCandidates };
};
